import { useState } from 'react';
import { useInvalidateQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface UseCreateRevisionDraftResult {
  /** 성공하면 true — 상세를 다시 불러오면 새 초안이 편집기에 뜬다. */
  readonly createRevisionDraft: (quoteRequestId: string) => Promise<boolean>;
  readonly isPending: boolean;
  readonly error: string | null;
}

const ERROR_FALLBACK = '재발행 초안을 만들지 못했습니다.';

/**
 * 재발행 초안 만들기(quoteManagement.createRevisionDraft, 견적 설계 §6-5) — 서버가 최신 발행본을 복사한다(멱등).
 * 상태 충돌이면 문구를 보이고 상세를 다시 불러와 버튼 조건을 새로 판단한다.
 */
export const useCreateRevisionDraft = (): UseCreateRevisionDraftResult => {
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateQuotes();
  const mutation = trpc.quoteManagement.createRevisionDraft.useMutation();

  const createRevisionDraft = async (quoteRequestId: string): Promise<boolean> => {
    setError(null);

    try {
      await mutation.mutateAsync({ quoteRequestId });
      await invalidate.detail(quoteRequestId);
      return true;
    } catch (cause) {
      setError(getErrorMessage(cause, ERROR_FALLBACK));

      if (getTrpcErrorCode(cause) === 'CONFLICT') {
        await invalidate.detail(quoteRequestId);
      }

      return false;
    }
  };

  return { createRevisionDraft, isPending: mutation.isPending, error };
};
